import {
  Component,
  ChangeDetectionStrategy,
  Input,
  EventEmitter,
  Output,
  forwardRef,
  SimpleChanges,
  OnChanges,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { IUserProfile } from '@store/common/user.interface';
import { MenuAction, MenuComponent } from '../menu/menu.component';
import { TranslateModule } from '@ngx-translate/core';
import { IdFormatPipe } from '@core/pipes/id-format.pipe';
import { JsonStringifyPipe } from '@core/pipes/json-stringify.pipe';
import { MatChipsModule } from '@angular/material/chips';
import { MatListModule } from '@angular/material/list';
import { MatIconModule } from '@angular/material/icon';
import { MatRadioModule } from '@angular/material/radio';
import { AddressSuggestionComponent } from '../address-suggestion/address-suggestion.component';
import { PbbSvgIconComponent } from '../pbb-svg-icon/pbb-svg-icon.component';
import { PbbRadioComponent } from '../pbb-radio';
import { PbbButtonComponent } from '../pbb-button/pbb-button.component';
import { AccountType } from '@api/index';
import { PbbNoticeComponent } from '../pbb-notice/pbb-notice.component';
import { NOTICE_TYPE } from '@core/constants';
import { PbbBankInfoComponent } from '../pbb-bank-info/pbb-bank-info.component';
import {StickyButtonDirective} from '@core/directives/btn-sticky.directive';

export const ACTION = {
  UNLINK: 'unlink',
  UNLINK_LAST: 'unlink_last',
  DEFAULT: 'default',
};

export interface SelectedAccount {
  selectedAccountId: string;
  selectedFiId: string;
  selectedFiUserId: string;
}

@Component({
  selector: 'pbb-payment-method-list',
  templateUrl: './payment-method-list.component.html',
  styleUrls: ['./payment-method-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PaymentMethodListComponent),
      multi: true,
    },
  ],
  imports: [
    CommonModule,
    AddressSuggestionComponent,
    MenuComponent,
    MatIconModule,
    MatListModule,
    MatChipsModule,
    MatRadioModule,
    ReactiveFormsModule,
    FormsModule,
    IdFormatPipe,
    JsonStringifyPipe,
    TranslateModule,
    PbbSvgIconComponent,
    PbbRadioComponent,
    PbbButtonComponent,
    PbbNoticeComponent,
    PbbBankInfoComponent,
    StickyButtonDirective
  ],
})
export class PaymentMethodListComponent implements ControlValueAccessor, OnChanges {
  noticeType = NOTICE_TYPE;
  selectedAccount: string | undefined;
  @Input() defaultSelectedAccount!: string | null;
  @Input() userProfile!: IUserProfile | undefined;
  @Input() isCheckout = false;
  @Input() displayButtons = true;
  @Output() menuAction: EventEmitter<MenuAction> = new EventEmitter();
  @Output() continue: EventEmitter<void> = new EventEmitter();
  Action = ACTION;
  accountType = AccountType;

  showSave = false;
  showContinue = false;

  private onChange = (value: any) => { };
  private onTouched = () => { };

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['defaultSelectedAccount'] || changes['isCheckout']) {
      this.updateShowSave();
    }
  }

  constructor(private readonly cdr: ChangeDetectorRef) { }

  saveSelection() {
    if (this.selectedAccount) {
      try {
        const account = JSON.parse(this.selectedAccount) as SelectedAccount;

        this.menuAction.emit({
          action: ACTION.DEFAULT,
          item: {
            linkedFiRef: account.selectedFiId,
            paymentMethodRef: account.selectedAccountId,
            fi_account_ref: account.selectedAccountId,
            fi_user_id: account.selectedFiUserId,
          },
        });

        this.defaultSelectedAccount = this.selectedAccount;
        this.updateShowSave();
      } catch (error) {
        console.error('Error parsing selected account:', error);
      }
    }
  }

  onContinue() {
    if (this.isCheckout) {
      this.saveSelection();
    }
    this.continue.emit();
  }

  private updateShowSave() {
    let show = false;
    let showContinueBtn = false;

    if (this.isCheckout) {
      showContinueBtn = this.selectedAccount !== this.defaultSelectedAccount;
    } else {
      show = this.selectedAccount !== this.defaultSelectedAccount;
    }

    if (this.showSave !== show || this.showContinue !== showContinueBtn) {
      this.showSave = show;
      this.showContinue = showContinueBtn;
      this.cdr.markForCheck();
    }
  }

  writeValue(value: any): void {
    this.selectedAccount = value;
    this.updateShowSave();
    this.cdr.markForCheck();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  onSelectionChange(value: string): void {
    this.selectedAccount = value;
    this.onChange(value);
    this.onTouched();
    this.updateShowSave();
  }
}
